import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDateTime } from 'components/IAP/Resources/utils/dateUtils';
import {
  awaitingResponse,
  channelsLabel,
  escalationLabel,
  isCancellable,
  NotificationSendDto,
  SEND_STATUS_LABEL,
  sendStatusTone,
  sendTimestamp,
  sendTitle,
  SOURCE_LABEL,
} from './notificationSendsModel';

const SEARCH_LABEL = 'Search sends…';
const COLUMNS = 8;

const TONE_VARIANT: Record<
  string,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  good: 'default',
  warn: 'secondary',
  bad: 'destructive',
  neutral: 'outline',
};

/**
 * Presentational: takes sends that are already filtered to a tab, so it stays
 * free of the query chain behind the dashboard.
 *
 * The counts are per recipient rather than per attempt, which is why "reached"
 * can be lower than the number of channels used and still be correct.
 */
export function NotificationSendsTable({
  sends,
  search,
  onSearchChange,
  onOpenSend,
  onCancelSend,
  emptyMessage,
}: {
  sends: NotificationSendDto[];
  search: string;
  onSearchChange: (next: string) => void;
  onOpenSend: (send: NotificationSendDto) => void;
  onCancelSend?: (send: NotificationSendDto) => void;
  emptyMessage: string;
}) {
  return (
    <div className="tw:space-y-2">
      <Input
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={SEARCH_LABEL}
        aria-label={SEARCH_LABEL}
        className="tw:h-8"
      />
      <div className="tw:overflow-x-auto tw:rounded-md tw:border tw:border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Subject</TableHead>
              <TableHead className="tw:w-[150px]">When</TableHead>
              <TableHead className="tw:w-[120px]">Source</TableHead>
              <TableHead className="tw:w-[150px]">Channels</TableHead>
              <TableHead className="tw:w-[110px]">Recipients</TableHead>
              <TableHead className="tw:w-[150px]">Delivery</TableHead>
              <TableHead className="tw:w-[110px]">Cascade</TableHead>
              <TableHead className="tw:w-[120px]">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sends.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMNS}
                  className="tw:text-center tw:text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              sends.map((send) => {
                const timestamp = sendTimestamp(send);
                const awaiting = awaitingResponse(send);
                const cascade = escalationLabel(send);

                return (
                  <TableRow
                    key={send.id}
                    className="tw:cursor-pointer"
                    onClick={() => onOpenSend(send)}
                  >
                    <TableCell>
                      <button
                        type="button"
                        className="tw:text-left tw:underline-offset-2 tw:hover:underline"
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenSend(send);
                        }}
                      >
                        {sendTitle(send)}
                      </button>
                      {send.createdByName ? (
                        <div className="tw:text-xs tw:text-muted-foreground">
                          {send.createdByName}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className="tw:text-xs">
                      {timestamp ? formatDateTime(timestamp) : '—'}
                    </TableCell>
                    <TableCell className="tw:text-xs">
                      {SOURCE_LABEL[send.source] ?? send.source}
                    </TableCell>
                    <TableCell className="tw:text-xs">
                      {channelsLabel(send.channels)}
                    </TableCell>
                    <TableCell className="tw:text-xs">
                      {send.stats.recipients}
                    </TableCell>
                    <TableCell className="tw:text-xs">
                      <span>{send.stats.reached} reached</span>
                      {send.stats.failed > 0 ? (
                        <span className="tw:text-destructive">
                          {' '}
                          · {send.stats.failed} failed
                        </span>
                      ) : null}
                      {awaiting > 0 ? (
                        <div className="tw:text-muted-foreground">
                          {awaiting} awaiting reply
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className="tw:text-xs">
                      {cascade || '—'}
                    </TableCell>
                    <TableCell>
                      <div className="tw:flex tw:flex-col tw:items-start tw:gap-1">
                        <Badge
                          variant={TONE_VARIANT[sendStatusTone(send.status)]}
                        >
                          {SEND_STATUS_LABEL[send.status] ?? send.status}
                        </Badge>
                        {onCancelSend && isCancellable(send) ? (
                          <Button
                            type="button"
                            size="xs"
                            variant="ghost"
                            onClick={(event) => {
                              event.stopPropagation();
                              onCancelSend(send);
                            }}
                          >
                            Cancel
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default NotificationSendsTable;
